import { Component, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CoreGridComponent } from 'src/app/shared/grid/core-grid/core-grid.component';
import { ColumnConfig } from 'src/app/shared/grid/core-grid/columnConfig.model';
import { SampleTemplate, SampleTemplateListResponse } from 'src/app/models/sampleTemplate.model';
import { SampleTemplateService } from 'src/app/services/sampleTemplate.service';
import { ToastService } from 'src/app/services/toast.service';
import { CommonService } from 'src/app/services/common.service';
import { AuthService } from 'src/app/services/auth.service';
import { GridViewService } from 'src/app/services/grid-view.service';
import { GridSettingsService } from 'src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { TranslateService } from 'src/app/services/translate.service';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { sampleRouteConfig } from 'src/app/shared/constants/sampleRouteConfig.constants';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { escapeHtml } from 'src/app/shared/helpers/html-escape.helper';
import { buildTemplateNameCell } from 'src/app/shared/helpers/template-name-cell.helper';
import { TemplateRowActions } from 'src/app/shared/helpers/template-row-actions.helper';
import { TemplateKindToggleComponent } from 'src/app/components/template-kind-toggle/template-kind-toggle.component';
import { SampleTypeEnum } from 'src/app/enums/sampleType.enum';
import { SampleDialogComponent } from 'src/app/modals/sample-dialog/sample-dialog.component';
import { SampleDialogTemplateEditData } from 'src/app/models/sampleTemplate.model';
import { EventService } from 'src/app/services/event.service';
import { EventKeys } from 'src/app/shared/constants/eventKeys.constants';
import { FlyoutContainerComponent } from 'src/app/shared/flyout/flyout-container/flyout-container.component';

@Component({
  standalone: false,
  selector: 'app-sample-template-list',
  templateUrl: './../../shared/grid/core-grid/core-grid.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./../../shared/grid/core-grid/core-grid.component.scss', './sample-template-list.component.scss']
})
export class SampleTemplateListComponent extends CoreGridComponent<SampleTemplateListResponse> {
  @ViewChild(FlyoutContainerComponent) flyout!: FlyoutContainerComponent;

  private readonly destroy$ = new Subject<void>();
  private readonly rowActions: TemplateRowActions<SampleTemplateListResponse, SampleTemplate>;

  constructor(
    protected sampleTemplateService: SampleTemplateService,
    toastService: ToastService,
    commonService: CommonService,
    dialog: MatDialog,
    authService: AuthService,
    gridViewService: GridViewService,
    gridSettingsService: GridSettingsService,
    private translateService: TranslateService,
    private organizationLevelService: OrganizationLevelService,
    private eventService: EventService
  ) {
    super(toastService, dialog, commonService, authService, gridViewService, gridSettingsService);
    this.gridService = sampleTemplateService;
    this.deleteDisabled = true;
    this.editDisabled = true;
    this.canCreate = false;
    this.rowActions = new TemplateRowActions<SampleTemplateListResponse, SampleTemplate>({
      service: sampleTemplateService,
      dialog,
      toastService,
      translate: key => this.translateService.getDynamicTranslation(key),
      destroy$: this.destroy$,
      reload: () => this.loadPage(),
      deleteTitle: 'Sample Template',
      edit: row => this.openEditFlyout(row),
      setDefault: row => sampleTemplateService.setDefault({ sampleType: row.sampleType, defaultSampleTemplateId: row.id }),
      clearDefault: row => sampleTemplateService.clearDefault(row.sampleType)
    });
    this.rowActionMenu = (row) => this.rowActions.buildMenuItems(row);
    this.toolbarComponent = TemplateKindToggleComponent;
  }

  protected get objectId(): string {
    return RolePermissionObjects.SampleTemplate;
  }

  protected override get isGridViewEnabled(): boolean {
    return true;
  }

  override initializeProperties(): void {
    this.addEntityTitle = 'Sample Template';
    this.searchPlaceholder = 'Search Templates';
    this.sortActive = nameOf<SampleTemplateListResponse>(x => x.name);
    this.defaultOrderBy = 'asc';
    this.rowActionMenuPanelClass = 'row-action-menu--compact';
  }

  override initializeColumns(): void {
    this.columnConfigs = [
      new ColumnConfig<SampleTemplateListResponse>({
        name: nameOf<SampleTemplateListResponse>(x => x.name),
        displayName: 'Name',
        cellTemplate: (row) => buildTemplateNameCell(row, key => this.translateService.getDynamicTranslation(key))
      }),
      new ColumnConfig<SampleTemplateListResponse>({
        name: nameOf<SampleTemplateListResponse>(x => x.sampleType),
        displayName: 'Type',
        isSortable: false,
        cellTemplate: (row) => SampleTypeEnum[row.sampleType] ?? '-'
      }),
      new ColumnConfig<SampleTemplateListResponse>({
        name: nameOf<SampleTemplateListResponse>(x => x.createdByUserName),
        displayName: 'Created By',
        cellTemplate: (row) => escapeHtml(row.createdByUserName) || '-'
      }),
      new ColumnConfig<SampleTemplateListResponse>({
        name: nameOf<SampleTemplateListResponse>(x => x.organizationLevelId),
        displayName: 'Visibility',
        isSortable: false,
        cellTemplate: (row) => row.isShared
          ? (escapeHtml(row.organizationLevelName) || '-')
          : this.translateService.getDynamicTranslation('Private')
      }),
      new ColumnConfig<SampleTemplateListResponse>({
        name: 'action',
        displayName: 'Actions',
        isSortable: false,
        isDisplayNameConfigurable: false
      })
    ];
  }

  protected override objectFilterMap(propertyName: string): OrganizationLevelService | undefined {
    if (propertyName === nameOf<SampleTemplateListResponse>(x => x.organizationLevelId)) {
      return this.organizationLevelService;
    }
    return undefined;
  }

  private openEditFlyout(row: SampleTemplateListResponse): void {
    const payload: SampleDialogTemplateEditData = {
      editSampleTemplateId: row.id,
      sampleType: row.sampleType
    };
    this.openFlyoutInternal(SampleDialogComponent, payload, '90vw');
  }

  override onRowClicked(row: SampleTemplateListResponse): void {
    if (this.isDialog) {
      super.onRowClicked(row);
      return;
    }
    if (row.canManage) {
      this.openEditFlyout(row);
    }
  }

  override initializeTabs() {
    return sampleRouteConfig;
  }

  override initializeOverviewConfigs(): void {
    this.overviewConfigs = [];
  }

  override getOverviewData(): Observable<void> {
    return of(undefined);
  }

  override openEntityDialog(_entityId?: string): void {
    // Creation is handled via the sample form's save-as flow, not from this grid.
  }

  override openEntityDeleteDialog(entity: SampleTemplateListResponse): void {
    this.rowActions.openDeleteDialog(entity);
  }

  override onFlyoutClose(): void {
    this.loadPage();
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.loadHeaderData();
    this.eventService.on<unknown>(EventKeys.flyoutClose)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.closeFlyoutInternal());
  }

  override ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    super.ngOnDestroy();
  }
}
